import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import {
    Body1,
    Body2,
    Box,
    Button,
    Flex,
    Heading1,
    HorizontalDivider,
    IconChevronRight,
    Image,
    OutlinedCard,
    Stack,
    Subtitle1,
    TextButton,
} from '@myphx-engineering/phx-design-system';
import { TalkativeWidget } from '@phx/talkative';

import { PageLayout } from '../../components';
import { useContactSupport } from '../../hooks/use-contact-support';
import { getEnvVar } from '../../utils/get-env-var';

type PartnerEntry = {
    appHome: string;
    logo: string;
    name: string;
    urlKey: string;
};

// My options were to hard-code Lilly Direct specifically or do something hacky
// like this. We don't know when the SSO app will get a real overhaul or
// replacement, so this seemed like the safer option.
const getPartnerUrl = (partner: PartnerEntry): string => {
    const base = getEnvVar(`VITE_${partner.urlKey}_UNAUTH_URL`);

    return `${base}${partner.appHome}`;
};

const InvalidLinkPage = () => {
    const { t } = useTranslation('sso');
    const { pathname } = useLocation();
    const myphxUrl = `${getEnvVar('VITE_MYPHX_UNAUTH_URL')}/home`;

    const onContactUsClick = useContactSupport();

    // instrument error state
    // oxlint-disable-next-line no-console
    console.error('Navigated to unknown route: ', pathname);

    const partners = (
        t('invalidLink.partner.partners', {
            returnObjects: true,
        }) as PartnerEntry[]
    ).toSorted((a, b) => a.name.localeCompare(b.name));

    return (
        <PageLayout
            footer={
                <Flex justify="center" gap="sm">
                    <TalkativeWidget onClick={onContactUsClick} />
                </Flex>
            }
        >
            <Stack gap="md">
                <Stack gap="xs">
                    <Heading1>{t('invalidLink.title')}</Heading1>
                    <Body1>{t('invalidLink.desc')}</Body1>
                </Stack>
                <Stack gap="xs">
                    <Stack gap="xxs">
                        <Subtitle1>{t('invalidLink.partner.title')}</Subtitle1>
                        <Body2>{t('invalidLink.partner.desc')}</Body2>
                    </Stack>
                    {partners.map((partner) => (
                        <OutlinedCard key={partner.logo}>
                            <Stack gap="xxxs">
                                <Flex justify="space-between">
                                    <Box>
                                        <Image
                                            src={`/static/img/${partner.logo}-logo.svg`}
                                            w="120"
                                            h="40"
                                            fit="contain"
                                        />
                                    </Box>
                                    <Box>
                                        <TextButton
                                            link
                                            p={0}
                                            RightIcon={IconChevronRight}
                                            to={getPartnerUrl(partner)}
                                        >
                                            {t('invalidLink.partner.signIn')}
                                        </TextButton>
                                    </Box>
                                </Flex>
                                <Body2>
                                    {t('invalidLink.partner.partnerDesc', {
                                        name: partner.name,
                                    })}
                                </Body2>
                            </Stack>
                        </OutlinedCard>
                    ))}
                </Stack>
                <HorizontalDivider />
                <Button link variant="outline" to={myphxUrl}>
                    {t('invalidLink.signIn')}
                </Button>
            </Stack>
        </PageLayout>
    );
};

export default InvalidLinkPage;
